import { useEffect, useRef, useState, useCallback } from 'react';
import { locationStorage, PendingLocation } from '../utils/locationStorage';
import api from '../utils/api';
import socketService from '../services/socketService';
import toast from 'react-hot-toast';

interface LocationData {
  lat: number;
  lng: number;
  velocidade?: number;
  direcao?: number;
  precisao?: number;
  altitude?: number;
}

interface UseLocationTrackingOptions {
  osId: number;
  faseId: number;
  enabled: boolean;
  interval?: number; // Intervalo mínimo entre envios (ms)
  onLocationUpdate?: (location: LocationData) => void;
}

/**
 * Hook para rastreamento de localização com suporte a background
 * Usa watchPosition, Wake Lock API, e IndexedDB para funcionar mesmo minimizado
 */
export const useLocationTracking = (options: UseLocationTrackingOptions) => {
  const { osId, faseId, enabled, interval = 15000, onLocationUpdate } = options;

  const [isTracking, setIsTracking] = useState(false);
  const [lastSentLocation, setLastSentLocation] = useState<LocationData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const watchIdRef = useRef<number | null>(null);
  const wakeLockRef = useRef<any>(null); // WakeLockSentinel pode não estar tipado
  const lastSentTimeRef = useRef<number>(0);
  const isVisibleRef = useRef<boolean>(true);

  /**
   * Solicitar Wake Lock para manter o dispositivo ativo
   */
  const requestWakeLock = useCallback(async (): Promise<void> => {
    if ('wakeLock' in navigator) {
      try {
        const wakeLock = await (navigator as any).wakeLock.request('screen');
        wakeLockRef.current = wakeLock;
        console.log('🔋 Wake Lock ativado');

        wakeLock.addEventListener('release', () => {
          console.log('🔋 Wake Lock liberado');
        });
      } catch (err: any) {
        console.warn('⚠️ Wake Lock não disponível:', err.message);
        // Não é crítico, continuar sem wake lock
      }
    }
  }, []);

  /**
   * Liberar Wake Lock
   */
  const releaseWakeLock = useCallback(async (): Promise<void> => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
        console.log('🔋 Wake Lock liberado manualmente');
      } catch (err) {
        console.warn('⚠️ Erro ao liberar Wake Lock:', err);
      }
    }
  }, []);

  /**
   * Enviar localização para o backend
   */
  const sendLocation = useCallback(
    async (location: LocationData): Promise<boolean> => {
      try {
        console.log('📤 Enviando localização para o backend:', {
          url: `/rastreamento/${osId}/fases/${faseId}/atualizar-posicao`,
          osId,
          faseId,
          location
        });

        // Enviar para o backend
        const response = await api.put(
          `/rastreamento/${osId}/fases/${faseId}/atualizar-posicao`,
          {
            lat: location.lat,
            lng: location.lng,
            velocidade: location.velocidade || 0,
            direcao: location.direcao || 0,
            precisao: location.precisao || 0,
            altitude: location.altitude || 0
          }
        );

        console.log('✅ Localização enviada para o backend com sucesso:', response.data);

        // Emitir evento WebSocket
        socketService.emitPositionUpdate({
          osId,
          faseId,
          lat: location.lat,
          lng: location.lng,
          velocidade: location.velocidade || 0,
          direcao: location.direcao || 0,
          precisao: location.precisao || 0,
          altitude: location.altitude || 0
        });

        setLastSentLocation(location);
        lastSentTimeRef.current = Date.now();
        return true;
      } catch (error: any) {
        console.error('❌ Erro ao enviar localização:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          statusText: error.response?.statusText,
          osId,
          faseId,
          location
        });

        // Se falhar, salvar no IndexedDB para sincronização posterior
        try {
          await locationStorage.savePendingLocation({
            osId,
            faseId,
            ...location
          });
          console.log('💾 Localização salva para sincronização posterior');

          // Registrar sync no Service Worker se disponível
          if ('serviceWorker' in navigator && 'sync' in (self as any).registration) {
            try {
              await (navigator.serviceWorker.ready as any).then(
                (registration: ServiceWorkerRegistration) => {
                  (registration as any).sync.register('sync-locations');
                  console.log('🔄 Background Sync registrado');
                }
              );
            } catch (syncError) {
              console.warn('⚠️ Background Sync não disponível:', syncError);
            }
          }
        } catch (storageError) {
          console.error('❌ Erro ao salvar localização no IndexedDB:', storageError);
        }

        return false;
      }
    },
    [osId, faseId]
  );

  /**
   * Sincronizar localizações pendentes
   */
  const syncPendingLocations = useCallback(async (): Promise<void> => {
    try {
      const pending = await locationStorage.getPendingLocations();
      console.log(`🔄 Sincronizando ${pending.length} localizações pendentes...`);

      for (const location of pending) {
        if (location.retryCount > 5) {
          // Remover após muitas tentativas
          await locationStorage.removePendingLocation(location.id);
          continue;
        }

        const success = await sendLocation({
          lat: location.lat,
          lng: location.lng,
          velocidade: location.velocidade,
          direcao: location.direcao,
          precisao: location.precisao,
          altitude: location.altitude
        });

        if (success) {
          await locationStorage.removePendingLocation(location.id);
        } else {
          await locationStorage.incrementRetryCount(location.id);
        }
      }
    } catch (error) {
      console.error('❌ Erro ao sincronizar localizações pendentes:', error);
    }
  }, [sendLocation]);

  /**
   * Verificar se deve enviar localização (respeitar intervalo mínimo)
   */
  const shouldSendLocation = useCallback((): boolean => {
    const now = Date.now();
    const timeSinceLastSend = now - lastSentTimeRef.current;
    return timeSinceLastSend >= interval;
  }, [interval]);

  /**
   * Handler para mudanças de visibilidade da página
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden;
      console.log(`👁️ Página ${isVisibleRef.current ? 'visível' : 'oculta'}`);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  /**
   * Iniciar rastreamento
   */
  const startTracking = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocalização não disponível');
      return;
    }

    // Limpar qualquer watchPosition órfão antes de iniciar novo
    if (watchIdRef.current !== null) {
      console.warn('⚠️ Limpando watchPosition órfão antes de iniciar novo...');
      try {
        navigator.geolocation.clearWatch(watchIdRef.current);
      } catch (error) {
        console.warn('⚠️ Erro ao limpar watchPosition:', error);
      }
      watchIdRef.current = null;
    }

    console.log('🚀 Iniciando rastreamento de localização...');

    // Solicitar Wake Lock
    requestWakeLock();

    // Inicializar IndexedDB
    locationStorage.init().catch((err) => {
      console.warn('⚠️ Erro ao inicializar IndexedDB:', err);
    });

    // Usar watchPosition ao invés de setInterval + getCurrentPosition
    watchIdRef.current = navigator.geolocation.watchPosition(
      async (position) => {
        const location: LocationData = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          velocidade: position.coords.speed ? position.coords.speed * 3.6 : undefined, // m/s para km/h
          direcao: position.coords.heading || undefined,
          precisao: position.coords.accuracy || undefined,
          altitude: position.coords.altitude || undefined
        };

        // Chamar callback se fornecido
        if (onLocationUpdate) {
          onLocationUpdate(location);
        }

        // Verificar se deve enviar (respeitar intervalo mínimo)
        if (shouldSendLocation()) {
          console.log('📍 Nova localização obtida, enviando...', location);
          const success = await sendLocation(location);
          if (!success) {
            console.warn('⚠️ Falha ao enviar localização, será salva para sincronização posterior');
          }
        } else {
          console.log('⏱️ Localização obtida, mas aguardando intervalo mínimo...');
        }
      },
      (error) => {
        console.error('❌ Erro no watchPosition:', error);
        let errorMessage = '';

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Permissão de localização negada';
            setError(errorMessage);
            // Parar watchPosition se permissão negada
            if (watchIdRef.current !== null) {
              navigator.geolocation.clearWatch(watchIdRef.current);
              watchIdRef.current = null;
              setIsTracking(false);
            }
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Localização indisponível';
            setError(errorMessage);
            // Não parar, pode ser temporário
            break;
          case error.TIMEOUT:
            errorMessage = 'Timeout ao obter localização - tentando novamente...';
            // Não definir erro permanente para timeout, pode ser temporário
            console.warn('⚠️ Timeout na geolocalização - continuando tentativas...');
            // Não mostrar toast para timeout, apenas log
            break;
          default:
            errorMessage = 'Erro ao obter localização';
            setError(errorMessage);
        }

        // Só mostrar toast para erros críticos (não timeout)
        if (error.code !== error.TIMEOUT) {
          toast.error(errorMessage);
        }
      },
      {
        enableHighAccuracy: false, // false = usar qualquer fonte (WiFi, rede, GPS) - mais rápido
        timeout: 30000, // 30 segundos - mais tempo para obter localização
        maximumAge: 60000 // Aceitar cache de até 1 minuto - evita timeout se já tem posição recente
      }
    );

    setIsTracking(true);
    setError(null);

    // Sincronizar localizações pendentes quando começar
    syncPendingLocations();
  }, [requestWakeLock, onLocationUpdate, shouldSendLocation, sendLocation, syncPendingLocations]);

  /**
   * Parar rastreamento
   */
  const stopTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      try {
        navigator.geolocation.clearWatch(watchIdRef.current);
        console.log('🛑 Rastreamento parado - watchPosition limpo');
      } catch (error) {
        console.warn('⚠️ Erro ao limpar watchPosition:', error);
      }
      watchIdRef.current = null;
    }

    releaseWakeLock();
    setIsTracking(false);
    setError(null); // Limpar erros ao parar
  }, [releaseWakeLock]);

  /**
   * Iniciar/parar baseado no estado enabled
   */
  useEffect(() => {
    // Usar refs para evitar loops
    const shouldBeTracking = enabled && !isTracking;
    const shouldStop = !enabled && isTracking;

    if (shouldBeTracking) {
      console.log('🚀 Estado mudou: enabled=true, iniciando rastreamento...', { osId, faseId });
      startTracking();
    } else if (shouldStop) {
      console.log('🛑 Estado mudou: enabled=false, parando rastreamento...', { osId, faseId });
      stopTracking();
    }

    // Cleanup: sempre limpar quando o componente desmontar ou enabled mudar para false
    return () => {
      if (watchIdRef.current !== null) {
        console.log('🧹 Cleanup: limpando watchPosition...');
        try {
          navigator.geolocation.clearWatch(watchIdRef.current);
        } catch (error) {
          console.warn('⚠️ Erro no cleanup do watchPosition:', error);
        }
        watchIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]); // Apenas dependência de enabled para evitar loops

  /**
   * Sincronizar ao voltar online
   */
  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 Conexão restaurada, sincronizando...');
      syncPendingLocations();
    };

    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [syncPendingLocations]);

  return {
    isTracking,
    lastSentLocation,
    error,
    startTracking,
    stopTracking,
    syncPendingLocations
  };
};
